<script setup>
import { Actions, EventManager, Events } from "@events";
import WebRTCRoom from "../../PureWebRTC/webrtcRoom.js";
import { useVideoCallState } from "@/stores/videoCallState";
import { computed, ref } from "vue";
import VideoContainer from "@/components/VideoCall/VideoContainer.vue";
import { sleep } from "@/utils";

const props = defineProps({
  rtcId: String,
});

let videoCallState = useVideoCallState(props.rtcId);
let videoCallEventEmitter = EventManager.getEventEmitter(props.rtcId);
let videoCallActionEmitter = EventManager.getActionEmitter(props.rtcId);
let rootEventEmitter = EventManager.rootEventEmitter();

let whisperRoom = null;
let whisperRoomState = null;
let whisperRoomEventEmitter = null;
let whisperRoomActionEmitter = null;
let whisperRtcId = ref(null);

let whisperInitiated = ref(false);
let whisperInitiator = ref(null);
let supervisorHidden = ref(true);
let isSmall = ref(true);

// listen to escape key press to close full screen
window.addEventListener("keydown", (e) => {
  if (e.key === "Escape") {
    isSmall.value = true;
  }
});

let whisperPopupHidden = computed(() => {
  return supervisorHidden.value || !whisperInitiated.value;
});



function initiate(data) {
  console.log("whisper:initiate", data);
  whisperInitiator.value = decodeURIComponent(data?.initiator);
  whisperRtcId.value = data.rtcId;

  whisperRoomState = useVideoCallState(whisperRtcId.value);
  whisperRoomState.setMuteEvents(true);
  whisperRoomEventEmitter = EventManager.getEventEmitter(whisperRtcId.value);
  whisperRoomActionEmitter = EventManager.getActionEmitter(whisperRtcId.value);

  whisperRoomState.setRole("agent");

  console.log(`Whisper initiated with ${whisperRtcId.value}. Creating a new room`);

  // Reuse the agent's already-background-processed video from the main call, so the supervisor still sees
  // the agent WITH background but we don't open the camera again or run a 2nd bg pipeline on this machine.
  const agentLocalVideoTrack = videoCallState.getPeerWithType("local")?.srcObject?.getVideoTracks?.()[0];

  const options = {
    // Audio-only acquire (no camera / no 2nd pipeline); the reused agent video is merged into the local
    // stream (via localVideoTrack) so the supervisor sees the agent as ONE tile, not a separate empty one.
    disableVideo: true,
    localVideoTrack: agentLocalVideoTrack,
    extraStreams: [videoCallState.getPeerWithType("remote").srcObject],
    recorderOptions: {
      whiteListPeers: ["remote"], // only record the remote peer (supervisor) stream

      // emit the current rtcId to the recorder
      // so it will be recorded as part of the current call recording 
      // and not a separate recording 
      overrideRtcId: videoCallState.rtcId, 

      // emit the current interactionId to the recorder
      // so it will be recorded as part of the current call recording 
      // and not a separate recording 
      overrideInteractionId: videoCallState.getInteractionId(),

      // remote peer will be recorded as "supervisor" in the recording metadata instead of "remote"
      // since we already have remote peer for the main call, and we want to distinguish between them in the recording metadata
      peerTypeOverrides: {
        "remote": "supervisor"
      }
    },
  };

  whisperRoomEventEmitter.on(Events.WebRTCConnected, () => {
    videoCallEventEmitter.on(Events.VideoCallEnded, async ({ hangupBy }) => {
      if (!supervisorHidden.value) {
        isSmall.value = false;
        whisperRoomState.setPreventPageClose(false);

        // Sending the remote user's id to supervisorWhisper to also disconnect it
        const remotePeer = videoCallState.peers.filter((peer) => peer.type == "remote")?.[0]; // the first one since it is the only one
        if (!remotePeer) {
          return;
        }
        console.log("remotePeer", remotePeer);

        const remotePeerVideoTrack = remotePeer?.video?.srcObject?.getTracks()?.filter((track) => track.kind == "video")?.[0];
        if (!remotePeerVideoTrack) {
          return;
        }
        console.log("remotePeerVideoTrack", remotePeerVideoTrack);

        whisperRoom.signaling.sendWrapped({
          type: "whisper:remove-user-stream",
          id: remotePeerVideoTrack?.id,
        });
      }
    });
  });

  whisperRoomState.setPreventPageClose(true);

  whisperRoom = new WebRTCRoom(whisperRtcId.value, options);

  rootEventEmitter.emit(Events.VideoCallCreated, {
    rtcId: whisperRtcId.value,
    ignoreWebRtcRoom: true,
  });

  videoCallEventEmitter.emit("whisper-initiated", {
    rtcId: whisperRtcId.value,
    room: whisperRoom,
  });

  whisperRoomEventEmitter.on(Events.WebRTCConnected, () => {
    videoCallEventEmitter.emit(Events.CallEvent, {
      name: "WhisperConnected",
      data: whisperRtcId.value + " | " + whisperInitiator.value,
    });
  });

  whisperRoomEventEmitter.on(Events.SignalingMessageReceived, (data) => {
    if (data.type === "whisper:set:hiddenMode:off") {
      supervisorHidden.value = false;
      videoCallState.setPreventPageClose(true);

      videoCallEventEmitter.emit(Events.CallEvent, {
        name: "WhisperRevealed",
        data: whisperRtcId.value + " | " + whisperInitiator.value,
      });
    } else if (data.type === "whisper:set:hiddenMode:on") {
      supervisorHidden.value = true;
      videoCallState.setPreventPageClose(false);

      videoCallEventEmitter.emit(Events.CallEvent, {
        name: "WhisperHidden",
        data: whisperRtcId.value + " | " + whisperInitiator.value,
      });
    } else if (data?.type == "whisper:photo-deleted") {
      videoCallActionEmitter.emit(Actions.PhotoPanelUpdate);
    }
  });

  whisperRoomEventEmitter.on(Events.VideoCallEnded, () => {
    videoCallState.setPreventPageClose(false);
    whisperInitiated.value = false;
    whisperInitiator.value = null;
    supervisorHidden.value = true;

    whisperRoom = null;
    whisperRoomState = null;
    whisperRoomEventEmitter = null;
    whisperRoomActionEmitter = null;

    whisperRtcId.value = null

    videoCallEventEmitter.emit(Events.CallEvent, {
      name: "WhisperEnded",
      data: whisperRtcId.value + " | " + whisperInitiator.value,
    });
  });

  whisperInitiated.value = true;
}

videoCallEventEmitter.on(Events.SignalingMessageReceived, (data) => {
  if (data.type === "whisper:initiate") {
    if (whisperInitiated.value) {
      handleNewWhisperWhenAlreadyInWhisper(data);
    } else {
      initiate(data);
    }
  }
});

videoCallEventEmitter.on(Events.PhotoTaken, (data) => {
  if (whisperRoom) {
    whisperRoom.signaling.sendWrapped({
      type: "whisper:photos-updated",
    });
  }
});

videoCallEventEmitter.on(Events.PhotoDeleted, (data) => {
  if (whisperRoom) {
    whisperRoom.signaling.sendWrapped({
      type: "whisper:photos-updated",
    });
  }
});

// this function is used when the agent receives a whisper:initiate event but the whisper is already initiated, 
// meaning that the agent has received multiple whisper:initiate events. 
// This can happen when the supervisor initiates a whisper and then initiates another one before the first one is ended. 
// In this case, we need to create a new room for the new whisper and send already-joined message to the supervisor 
// so it can end the new whisper immediately without creating a new room on its side,
// since the agent is already in a whisper with the supervisor.
function handleNewWhisperWhenAlreadyInWhisper(data) {
  console.log("whisper:already-initiated", data);

  const tmpWhisperRtcId = data.rtcId;
  const tmpWhisperRoomState = useVideoCallState(tmpWhisperRtcId);
  const tmpWhisperRoomEventEmitter = EventManager.getEventEmitter(tmpWhisperRtcId);

  tmpWhisperRoomState.setRole("agent");
  tmpWhisperRoomState.setPreventPageClose(true);

  console.log(`Whisper alreadyInitiated room created with rtcId: ${tmpWhisperRtcId}`);

  const options = {
    disableRecording: true,
    disableVideo: true,
    // can't disable audio from first init, because webrtc connection won't start then.
  };

  const tmpWhisperRoom = new WebRTCRoom(tmpWhisperRtcId, options);

  tmpWhisperRoomEventEmitter.on(Events.WebRTCConnected, async () => {
    // Disabled audio after its first moment connection
    tmpWhisperRoom.localStream.getTracks().forEach((track) => {
      if (track.kind === "audio") {
        track.enabled = false;
      }
    });

    tmpWhisperRoom.signaling.sendWrapped({
      type: "whisper:already-joined",
    });

    await sleep(1000);
    tmpWhisperRoom.roomActionController.hangUp({ hangupBy: "remote" });
  });
}
</script>

<template>
  <div :class="'agent-whisper-popup ' + (isSmall ? 'small' : '')" v-if="!whisperPopupHidden">
    <div class="whisper-container">
      <VideoContainer :hideLocalVideo="true" :rtc-id="whisperRtcId" :disableVideoMutedIcon="true"></VideoContainer>
      <span class="initiator-name">{{ whisperInitiator }}</span>
      <div class="status-indicators">
        <!-- whisperRoomState.remoteAudioMuted and whisperRoomState.remoteVideoMuted status indicators -->
        <v-icon class="indicator-icon" :class="{ 'right-upper': !whisperRoomState.remoteVideoMuted }" color="red"
          v-if="whisperRoomState.remoteAudioMuted">{{ whisperRoomState.remoteAudioMuted ? "mdi-microphone-off" :
            "mdi-microphone" }}</v-icon>
        <v-icon class="indicator-icon" color="red" v-if="whisperRoomState.remoteVideoMuted">{{
          whisperRoomState.remoteVideoMuted ? "mdi-video-off" : "mdi-video"
        }}</v-icon>
      </div>
      <div class="whisper-status-bar">
        <!-- Full screen -->
        <v-btn icon @click="isSmall = !isSmall" size="small" :class="{ 'small-btn': isSmall }">
          <v-icon>{{ isSmall ? "mdi-fullscreen" : "mdi-fullscreen-exit" }}</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<style>
.agent-whisper-popup {
  position: absolute;
  top: 0;
  right: 0px;
  z-index: 9999;
  width: 100%;
  display: flex;
  max-width: 100%;
  border: 1px solid red;
  border-radius: 3px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  flex-direction: column;
  transition: all 0.3s;
  align-content: center;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  background-color: #000;
}

.agent-whisper-popup.small {
  width: 200px;
  height: auto;
}

.whisper-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.whisper-status-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: transparent;
  color: #fff;
  text-align: center;
  padding-bottom: 5px;
}

.whisper-status-bar .small-btn {
  width: 30px !important;
  height: 30px !important;
}

.status-indicators {
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  gap: 10px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  color: #fff;
}

/* icons inside staus indicators border red circular */
.status-indicators>.indicator-icon {
  border: 1px solid red;
  border-radius: 50%;
  padding: 15px;
  /* light grey background */
  background-color: #000000c4;
}

.status-indicators .right-upper {
  position: absolute;
  top: 5px;
  right: 5px;
}

.initiator-name {
  position: absolute;
  top: 4px;
  left: 2px;
  width: auto;
  height: auto;
  gap: 10px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  color: #fff;
  background-color: #000;
  padding: 0px 5px;
}
</style>
